import type { ActionRequiredWaitSegment, ProjectTask } from '../types';
import { isActionRequiredExternal } from './actionRequiredDisplay';

type TaskArContext = Pick<
  ProjectTask,
  'status' | 'actionRequired' | 'actionRequiredNote' | 'actionRequiredWaitSegments' | 'createdAt' | 'assignedTo'
>;

function isEmptyAr(value: string | undefined | null): boolean {
  return value === undefined || value === null || value === '';
}

function arIdentityChanged(
  beforeAr: string | undefined,
  beforeNote: string | undefined,
  afterAr: string | undefined,
  afterNote: string | undefined,
): boolean {
  return beforeAr !== afterAr || (beforeNote || '').trim() !== (afterNote || '').trim();
}

function closeOpenSegment(segments: ActionRequiredWaitSegment[], endedAt: Date): ActionRequiredWaitSegment[] {
  const openIdx = segments.findIndex((s) => !s.endedAt);
  if (openIdx < 0) return segments;
  const next = [...segments];
  next[openIdx] = { ...next[openIdx], endedAt };
  return next;
}

function appendHolderSegment(
  segments: ActionRequiredWaitSegment[],
  holderKey: string,
  holderNote: string | undefined,
  startedAt: Date,
): ActionRequiredWaitSegment[] {
  return [
    ...segments,
    {
      holderKey,
      holderNote: holderNote?.trim() || undefined,
      startedAt,
    },
  ];
}

/**
 * Completing a task clears any action-required holder. Callers that only set
 * `status: 'completed'` (or progress 100) must still drop the flag everywhere
 * it appears.
 */
export function clearActionRequiredOnCompletion(
  task: TaskArContext,
  updates: Partial<ProjectTask>,
): Partial<ProjectTask> {
  const nextStatus = Object.prototype.hasOwnProperty.call(updates, 'status')
    ? updates.status
    : task.status;
  const completingViaProgress =
    nextStatus !== 'completed' &&
    task.status !== 'completed' &&
    Object.prototype.hasOwnProperty.call(updates, 'progress') &&
    (updates.progress ?? 0) >= 100;
  if (nextStatus !== 'completed' && !completingViaProgress) return updates;
  // Respect an explicit actionRequired write in the same patch.
  if (Object.prototype.hasOwnProperty.call(updates, 'actionRequired')) return updates;
  if (!task.actionRequired && !task.actionRequiredNote) return updates;
  return {
    ...updates,
    actionRequired: undefined,
    actionRequiredNote: undefined,
  };
}

/**
 * When action required is set: auto in-progress, auto-add internal assignee, and append wait segments.
 * When a task is completed: clear action required (unless the patch sets it explicitly).
 */
export function applyActionRequiredSideEffects(
  task: TaskArContext,
  updates: Partial<ProjectTask>,
): Partial<ProjectTask> {
  const withCompletionClear = clearActionRequiredOnCompletion(task, updates);

  if (!Object.prototype.hasOwnProperty.call(withCompletionClear, 'actionRequired')) {
    return withCompletionClear;
  }

  const afterAr = isEmptyAr(withCompletionClear.actionRequired)
    ? undefined
    : withCompletionClear.actionRequired;
  const afterNote = Object.prototype.hasOwnProperty.call(withCompletionClear, 'actionRequiredNote')
    ? withCompletionClear.actionRequiredNote
    : task.actionRequiredNote;

  if (
    !arIdentityChanged(task.actionRequired, task.actionRequiredNote, afterAr, afterNote)
  ) {
    return withCompletionClear;
  }

  const result: Partial<ProjectTask> = { ...withCompletionClear };
  const now = new Date();
  let segments = [...(task.actionRequiredWaitSegments || [])];
  segments = closeOpenSegment(segments, now);

  if (afterAr) {
    segments = appendHolderSegment(segments, afterAr, afterNote, now);

    if (task.status !== 'completed' && task.status !== 'in-progress' && result.status === undefined) {
      result.status = 'in-progress';
    }

    if (!isActionRequiredExternal(afterAr)) {
      const currentAssigned = result.assignedTo ?? task.assignedTo ?? [];
      if (!currentAssigned.includes(afterAr)) {
        result.assignedTo = [...currentAssigned, afterAr];
      }
    }
  }

  result.actionRequiredWaitSegments = segments;
  return result;
}

/** Seed the first wait segment when a task is created with action required already set. */
export function seedActionRequiredWaitSegmentsOnCreate(
  task: Pick<ProjectTask, 'actionRequired' | 'actionRequiredNote' | 'createdAt'>,
): ActionRequiredWaitSegment[] | undefined {
  if (!task.actionRequired) return undefined;
  return [
    {
      holderKey: task.actionRequired,
      holderNote: task.actionRequiredNote?.trim() || undefined,
      startedAt: task.createdAt,
    },
  ];
}

/** Firestore-safe segment rows — omit undefined optional fields. */
export function serializeActionRequiredWaitSegmentsForFirestore(
  segments: ActionRequiredWaitSegment[],
  Timestamp: { fromDate: (d: Date) => unknown },
): Record<string, unknown>[] {
  return segments.map((seg) => ({
    holderKey: seg.holderKey,
    ...(seg.holderNote?.trim() ? { holderNote: seg.holderNote.trim() } : {}),
    startedAt: Timestamp.fromDate(seg.startedAt),
    ...(seg.endedAt ? { endedAt: Timestamp.fromDate(seg.endedAt) } : {}),
  }));
}
